import React from 'react';
import { Box, Typography, Slider, Paper } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

interface ThresholdSliderProps {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  unit?: string;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export const ThresholdSlider: React.FC<ThresholdSliderProps> = ({
  value,
  min = 0,
  max = 100,
  step = 1,
  label = 'Threshold',
  unit = '%',
  onChange,
  disabled = false
}) => {
  const handleChange = (_event: Event, newValue: number | number[]) => {
    onChange(Array.isArray(newValue) ? newValue[0] : newValue);
  };

  const formatValue = (val: number) => {
    return `${val}${unit}`;
  };

  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight="medium">
            {label}
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Typography 
              variant="h6" 
              color="primary.main" 
              fontWeight="bold"
              sx={{ 
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                minWidth: 60,
                textAlign: 'center'
              }}
            >
              {formatValue(value)}
            </Typography>
          </Box>
        </Box>

        {/* Slider */}
        <Box sx={{ px: 1 }}>
          <Slider
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={handleChange}
            disabled={disabled}
            valueLabelDisplay="auto"
            valueLabelFormat={formatValue}
            sx={{
              '& .MuiSlider-thumb': {
                height: 20,
                width: 20,
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0px 0px 0px 8px rgba(25, 118, 210, 0.16)',
                }
              },
              '& .MuiSlider-track': {
                height: 6,
                borderRadius: 3
              },
              '& .MuiSlider-rail': {
                height: 6,
                borderRadius: 3,
                opacity: 0.3
              },
              '& .MuiSlider-valueLabel': {
                lineHeight: 1.2,
                fontSize: 12,
                background: 'unset',
                padding: 0,
                width: 32,
                height: 32,
                borderRadius: '50% 50% 50% 0',
                backgroundColor: 'primary.main',
                transformOrigin: 'bottom left',
                transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
                '&:before': { display: 'none' },
                '&.MuiSlider-valueLabelOpen': {
                  transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
                },
                '& > *': {
                  transform: 'rotate(45deg)',
                },
              }
            }}
          />
        </Box>

        {/* Range Display */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1
        }}>
          <Typography variant="caption" color="text.secondary">
            Min: {formatValue(min)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Max: {formatValue(max)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}; 